import { useState } from 'react';
import useFetch from '../../../hooks/useFetch';
import { buildEndpoint } from '../EndPoints';
import { ServiceEvents } from '../shared/interfaces';

const useChecklistService = () => {
    const { doGet, doPost } = useFetch();
    
    // Estados de carga - Admin
    const [fetchingGetChecklists, setFetchingGetChecklists] = useState(false);
    const [fetchingStoreChecklist, setFetchingStoreChecklist] = useState(false);
    const [fetchingShowChecklist, setFetchingShowChecklist] = useState(false);
    const [fetchingUpdateChecklist, setFetchingUpdateChecklist] = useState(false);
    const [fetchingDeleteChecklist, setFetchingDeleteChecklist] = useState(false);
    const [fetchingActiveChecklist, setFetchingActiveChecklist] = useState(false);
    
    // Estados de carga - Usuario
    const [fetchingAvailableChecklists, setFetchingAvailableChecklists] = useState(false);
    const [fetchingStartChecklist, setFetchingStartChecklist] = useState(false);
    const [fetchingSubmitChecklist, setFetchingSubmitChecklist] = useState(false);
    const [fetchingMyResponses, setFetchingMyResponses] = useState(false);
    const [fetchingResponseDetail, setFetchingResponseDetail] = useState(false);
    
    const BASE_PATH = 'checklists';
    
    // ==================== ADMIN - CRUD ====================
    
    /**
     * Obtener listado de checklists (admin)
     */
    const getChecklists = (filters: Record<string, any> = {}, events: ServiceEvents = {}) => {
        const queryString = Object.entries(filters)
            .filter(([_, value]) => value !== undefined && value !== '')
            .map(([key, value]) => `${key}=${value}`)
            .join('&');
            
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/admin/checklists${queryString ? `?${queryString}` : ''}`),
            setFetching: setFetchingGetChecklists
        });
    };
    
    /**
     * Crear checklist (admin)
     */
    const storeChecklist = (data: any, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/admin/checklists/store`),
            body: data,
            requestType: 'json',
            setFetching: setFetchingStoreChecklist
        });
    };
    
    /**
     * Ver detalle de checklist (admin)
     */
    const showChecklist = (id: number, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/admin/checklists/${id}/show`),
            setFetching: setFetchingShowChecklist
        });
    };
    
    /**
     * Actualizar checklist (admin)
     */
    const updateChecklist = (id: number, data: any, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/admin/checklists/${id}/update`),
            body: data,
            requestType: 'json',
            setFetching: setFetchingUpdateChecklist
        });
    };
    
    /**
     * Eliminar checklist (admin)
     */
    const deleteChecklist = (id: number, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/admin/checklists/${id}/delete`),
            body: {},
            setFetching: setFetchingDeleteChecklist
        });
    };
    
    /**
     * Activar/Desactivar checklist (admin)
     */
    const activeChecklist = (id: number, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/admin/checklists/${id}/active`),
            body: {},
            setFetching: setFetchingActiveChecklist
        });
    };
    
    // ==================== USUARIO ====================
    
    /**
     * Obtener checklists disponibles para responder
     */
    const getAvailableChecklists = (filters: Record<string, any> = {}, events: ServiceEvents = {}) => {
        const queryString = Object.entries(filters)
            .filter(([_, value]) => value !== undefined && value !== '')
            .map(([key, value]) => `${key}=${value}`)
            .join('&');
            
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/available${queryString ? `?${queryString}` : ''}`),
            setFetching: setFetchingAvailableChecklists
        });
    };
    
    /**
     * Obtener checklist para empezar a responder
     */
    const startChecklist = (id: number, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/${id}/start`),
            setFetching: setFetchingStartChecklist
        });
    };
    
    /**
     * Enviar respuestas del checklist
     */
    const submitChecklist = (data: { checklist_id: number; answers: Array<{ question_id: number; option_ids: number[] }> }, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/submit`),
            body: data,
            requestType: 'json',
            setFetching: setFetchingSubmitChecklist
        });
    };
    
    /**
     * Obtener mis respuestas de checklists
     */
    const getMyResponses = (filters: Record<string, any> = {}, events: ServiceEvents = {}) => {
        const queryString = Object.entries(filters)
            .filter(([_, value]) => value !== undefined && value !== '')
            .map(([key, value]) => `${key}=${value}`)
            .join('&');
            
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/my-responses${queryString ? `?${queryString}` : ''}`),
            setFetching: setFetchingMyResponses
        });
    };
    
    /**
     * Ver detalle de una respuesta
     */
    const getResponseDetail = (id: number, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/responses/${id}`),
            setFetching: setFetchingResponseDetail
        });
    };
    
    return {
        // Admin - Métodos
        getChecklists,
        storeChecklist,
        showChecklist,
        updateChecklist,
        deleteChecklist,
        activeChecklist,
        // Admin - Estados
        fetchingGetChecklists,
        fetchingStoreChecklist,
        fetchingShowChecklist,
        fetchingUpdateChecklist,
        fetchingDeleteChecklist,
        fetchingActiveChecklist,
        // Usuario - Métodos
        getAvailableChecklists,
        startChecklist,
        submitChecklist,
        getMyResponses,
        getResponseDetail,
        // Usuario - Estados
        fetchingAvailableChecklists,
        fetchingStartChecklist,
        fetchingSubmitChecklist,
        fetchingMyResponses,
        fetchingResponseDetail
    };
};

export default useChecklistService;

